import React, { useState } from 'react';
import { Accordion, Button, Col, Form, Modal, Row } from 'react-bootstrap';
import { formatarValor, formatarValorLocal } from '../../common/utils';
import OSAvulsaPagamentoForm from './OSAvulsaPagamentoForm';

const LABELS_STATUS_FATURAMENTO_AVULSO = {
    aguardando_pagamento: 'Aguardando Pagamento',
    solicitado_pagamento: 'Solicitado Pagamento',
    aguardando_nota_fiscal: 'Aguardando Nota Fiscal',
    finalizado: 'Finalizado',
    estorno: 'Estorno',
};

const CLASSES_STATUS_FATURAMENTO_AVULSO = {
    aguardando_pagamento: 'bg-light text-dark border',
    solicitado_pagamento: 'bg-warning text-dark',
    aguardando_nota_fiscal: 'bg-secondary',
    finalizado: 'bg-success',
    estorno: 'bg-danger',
};

function ModalEntendaComoFunciona({ show, onHide }) {
    return (
        <Modal show={show} onHide={onHide} centered scrollable size="lg">
            <Modal.Header closeButton>
                <Modal.Title>Como funciona o Faturamento do Exame Avulso</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <h6>O que é um exame avulso?</h6>
                <p>
                    É um exame realizado num laboratório ou clínica que não tem contrato com o
                    Programa. O cadastro desse local é feito na hora, direto na tela de Exames,
                    sem precisar de documentação contratual.
                </p>

                <h6>Como gerar um exame avulso</h6>
                <ol>
                    <li>Marque a opção <strong>&ldquo;Procedimento Avulso?&rdquo;</strong> no exame.</li>
                    <li>Cadastre o laboratório/clínica (CNPJ, endereço, etc.), se ainda não estiver cadastrado.</li>
                    <li>Informe o <strong>Valor Unitário do Exame</strong> e a <strong>Data Limite de Pagamento</strong>.</li>
                    <li>Quando o exame for marcado como <strong>Realizado</strong>, ele fica pronto para ser faturado.</li>
                </ol>

                <h6>Como a Ordem de Serviço (OS) é gerada</h6>
                <p>
                    Mais abaixo, na seção <strong>&ldquo;Faturamento de Exames Avulsos&rdquo;</strong>,
                    os exames avulsos ainda não faturados deste pedido aparecem agrupados por
                    laboratório — um cartão para cada laboratório. Marque os exames desejados
                    (ou use o checkbox &ldquo;selecionar todos&rdquo; do cartão) e clique em{' '}
                    <strong>&ldquo;Gerar Faturamento&rdquo;</strong>. Todos os exames marcados
                    daquele cartão viram <strong>uma única Ordem de Serviço</strong> — assim como o
                    laboratório manda uma nota só cobrindo todos os exames daquele pedido no local,
                    e não uma nota por exame. Se o pedido tiver exames em mais de um laboratório,
                    cada um vira sua própria OS, gerada separadamente pelo cartão correspondente.
                </p>
                <p>
                    Se já existir uma Ordem de Serviço em aberto para aquele laboratório, o sistema
                    pergunta se você quer incluir os exames selecionados nela em vez de criar uma
                    nova. Se o laboratório não informa o valor de cada exame separadamente, marque
                    <strong> &ldquo;Informar valor total manualmente&rdquo;</strong> antes de gerar.
                </p>
                <p>
                    Depois de gerada, pode aparecer aqui mesmo um formulário para registrar o
                    pagamento (forma de pagamento, dados bancários/PIX, comprovante, data) e a nota
                    fiscal (arquivo, número, emissão, vencimento) — sem precisar acessar a tela de
                    Faturamento. Anexar o comprovante junto com a data do pagamento já marca a Ordem
                    de Serviço como paga automaticamente. Há também um botão{' '}
                    <strong>&ldquo;Editar OS&rdquo;</strong> que abre a tela de{' '}
                    <strong>Faturamento</strong> (uso do Financeiro), com mais detalhes e controles.
                </p>

                <h6>O que significa cada status</h6>
                <ul>
                    <li><strong>Aguardando Pagamento</strong> — o exame ainda não foi incluído em nenhuma Ordem de Serviço.</li>
                    <li><strong>Solicitado Pagamento</strong> — já existe uma Ordem de Serviço criada, aguardando o pagamento ser efetivado.</li>
                    <li><strong>Aguardando Nota Fiscal</strong> — o pagamento já foi feito, falta o laboratório enviar a nota fiscal.</li>
                    <li><strong>Finalizado</strong> — nota fiscal recebida e aprovada. Processo concluído.</li>
                    <li><strong>Estorno</strong> — o pagamento foi cancelado/devolvido.</li>
                </ul>

                <h6>Onde acompanhar</h6>
                <p className="mb-0">
                    Use o botão <strong>&ldquo;Ver Faturamento&rdquo;</strong>, quando disponível, para abrir a
                    Ordem de Serviço e ver todos os detalhes do pagamento.
                </p>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onHide}>Fechar</Button>
            </Modal.Footer>
        </Modal>
    );
}

function ExamePagamentoAvulsoSection({ exame, onChange, formasPagamento, ocultarEdicaoOS, onOSAtualizada }) {
    const [showAjuda, setShowAjuda] = useState(false);

    const dadosFaturamento = exame.dados_faturamento || {};
    const statusDerivado = dadosFaturamento.status_derivado || 'aguardando_pagamento';

    const hoje = new Date();
    const dataMinima = hoje.toISOString().split('T')[0];
    const dataMaximaObj = new Date(hoje);
    dataMaximaObj.setMonth(dataMaximaObj.getMonth() + 3);
    const dataMaxima = dataMaximaObj.toISOString().split('T')[0];

    const abrirAjuda = (event) => {
        event.stopPropagation();
        setShowAjuda(true);
    };

    return (
        <Col sm={12}>
            <Accordion className="mb-3">
                <Accordion.Item eventKey="0" className="border">
                    <Accordion.Header>
                        <div className="d-flex align-items-center justify-content-between w-100">
                            <div className="d-flex align-items-center gap-2">
                                <strong>Faturamento do Exame Avulso</strong>
                                <span
                                    className="badge rounded-circle bg-secondary"
                                    role="button"
                                    title="Entenda como funciona"
                                    onClick={abrirAjuda}
                                >
                                    ?
                                </span>
                            </div>
                            <span className={`badge ${CLASSES_STATUS_FATURAMENTO_AVULSO[statusDerivado] || 'bg-secondary'}`}>
                                {LABELS_STATUS_FATURAMENTO_AVULSO[statusDerivado] || statusDerivado}
                            </span>
                        </div>
                    </Accordion.Header>
                    <Accordion.Body className="bg-light">
                        {dadosFaturamento.id ? (
                            <div className="d-flex align-items-center gap-2 flex-wrap mb-3">
                                <span className="small text-muted">
                                    Faturado na Ordem de Serviço #{dadosFaturamento.id}
                                    {dadosFaturamento.valor_total != null && ` — Valor: ${formatarValorLocal(dadosFaturamento.valor_total)}`}
                                </span>
                                <a
                                    className="btn btn-outline-secondary btn-sm"
                                    href={`/faturamentos/edit/${dadosFaturamento.id}`}
                                    target="_blank"
                                    rel="noreferrer"
                                >
                                    Ver Faturamento
                                </a>
                            </div>
                        ) : (
                            <div className="small text-muted mb-3">
                                Ainda não faturado. Forma de pagamento, comprovante e nota fiscal só podem
                                ser preenchidos depois que a Ordem de Serviço for gerada.
                            </div>
                        )}

                        {dadosFaturamento.id && !dadosFaturamento.cancelado && !ocultarEdicaoOS && (
                            <OSAvulsaPagamentoForm
                                faturamentoId={dadosFaturamento.id}
                                dadosFaturamento={dadosFaturamento}
                                formasPagamento={formasPagamento}
                                onSalvo={onOSAtualizada}
                            />
                        )}

                        <Row>
                            <Col sm={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Valor Unitário do Exame</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={exame.orcamento || ''}
                                        onChange={event => onChange({ orcamento: formatarValor(event.target.value) })}
                                        placeholder="0,00"
                                    />
                                </Form.Group>
                            </Col>

                            <Col sm={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Data Limite de Pagamento</Form.Label>
                                    <Form.Control
                                        type="date"
                                        min={dataMinima}
                                        max={dataMaxima}
                                        value={exame.data_limite_pagamento || ''}
                                        onChange={event => onChange({ data_limite_pagamento: event.target.value || null })}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                    </Accordion.Body>
                </Accordion.Item>
            </Accordion>

            <ModalEntendaComoFunciona show={showAjuda} onHide={() => setShowAjuda(false)} />
        </Col>
    );
}

export default ExamePagamentoAvulsoSection;
